import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { bootstrapApplication } from '@angular/platform-browser';
// eslint-disable-next-line sonarjs/deprecation
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { HarnessFacade } from '@app/+state/feature-flags/harness.facade';
import { UserAccessEffects } from '@app/+state/user-access';
import { ENV_VARS } from '@app/models/config.model';
import { getEnv } from '@app/v2/shared/utils';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import { CustomSerializer, GRID_PREFERRED_LANGUAGE$ } from '@lemans/shared';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  ControlPageEffects,
  GainsightEffects,
  initApplication,
  ModalEffects,
  reducers,
  UiStateEffects,
  UserDataEffects,
  UserDataFacade,
} from '@ra-state';
import { DomHandler, STRING_DATE_ADAPTER_CONFIG, TypographyService } from '@ra-web-tech-ui-toolkit/components';
import { LayoutModule, MultiTranslateHttpLoader, TranslationAsset } from '@rockwell-automation-inc/layout';
import { AuthErrorInterceptor, ServiceModule } from '@rockwell-automation-inc/service';
import { AppInsightErrorHandlerService } from '@servicesV2/app-insight-error-handler.service';
import { LoggerService } from '@servicesV2/logger.service';
import { AgGridModule } from 'ag-grid-angular';
import {
  AllCommunityModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ModuleRegistry,
} from 'ag-grid-community';
import {
  LicenseManager,
  MasterDetailModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  TreeDataModule,
} from 'ag-grid-enterprise';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Observable } from 'rxjs';
import { AccessRequestsEffects } from './app/+state/access-requests';
import { FeatureFlagsEffects } from './app/+state/feature-flags';
import { GainsightService } from './app/+state/gainsight/gainsight.service';
import { InvitationEffects } from './app/+state/invitation';
import { SignalREffects } from './app/+state/signalR/signalr.effects';
import { TrialsEffects } from './app/+state/trials/trials.effects';
import { appRoutes } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { LoaderInterceptor } from './app/interceptors/loader.interceptor';
import { MaintenanceWindowInterceptor } from './app/interceptors/maintenance-window.interceptor';
import { RequestIdInterceptor } from './app/interceptors/reqId.interceptor';
import { SignalRConnectionInterceptor } from './app/interceptors/signalr-connection.interceptor';
import { ModalService } from './app/v2/core/modal/modal.service';
import { DataService } from './app/v2/core/services/data.service';
import {
  CS_HARNESS_KEY,
  HarnessService,
  SERVICE_FLAGS_HARNESS,
  SERVICE_HARNESS_KEY,
} from './app/v2/core/services/harness.service';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient): MultiTranslateHttpLoader {
  const translationResourceList: TranslationAsset[] = [
    { path: './assets/i18n/' },
    { path: '@ra-web-tech-ui-toolkit/components/assets/locals/' },
  ];
  return new MultiTranslateHttpLoader(http, translationResourceList);
}

if (environment.production) {
  enableProdMode();
}

LicenseManager.setLicenseKey(getEnv(ENV_VARS.AG_GRID_KEY));

ModuleRegistry.registerModules([
  AllCommunityModule,
  ServerSideRowModelModule,
  ClientSideRowModelModule,
  ServerSideRowModelApiModule,
  ClientSideRowModelApiModule,
  TreeDataModule,
  MasterDetailModule,
]);

bootstrapApplication(AppComponent, {
  // NOSONAR -- Target browsers do not support top-level await.
  providers: [
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      NgScrollbarModule,
      AgGridModule,
      StoreModule.forRoot(reducers),
      StoreDevtoolsModule.instrument({ logOnly: environment.production }),
      StoreRouterConnectingModule.forRoot({
        serializer: CustomSerializer,
      }),
      AuthModule.forRoot(),
      LayoutModule,
      ServiceModule.forRoot(environment.appConfiguration),
      EffectsModule.forRoot([
        ControlPageEffects,
        GainsightEffects,
        UserDataEffects,
        ModalEffects,
        FeatureFlagsEffects,
        InvitationEffects,
        AccessRequestsEffects,
        TrialsEffects,
        UiStateEffects,
        SignalREffects,
        UserAccessEffects,
      ]),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        fallbackLang: 'en',
      }),
    ),
    // CoreModule providers
    DataService,
    {
      provide: GRID_PREFERRED_LANGUAGE$,
      useFactory: (userDataFacade: UserDataFacade): Observable<string> => userDataFacade.preferredLanguage$,
      deps: [UserDataFacade],
    },
    {
      provide: HarnessService,
      useFactory: (harnessFacade: HarnessFacade, logger: LoggerService, apiKey: string): HarnessService =>
        new HarnessService(harnessFacade, logger, apiKey),
      deps: [HarnessFacade, LoggerService, CS_HARNESS_KEY],
    },
    {
      provide: SERVICE_FLAGS_HARNESS,
      useFactory: (harnessFacade: HarnessFacade, logger: LoggerService, apiKey: string): HarnessService =>
        new HarnessService(harnessFacade, logger, apiKey, 'Service-Flags'),
      deps: [HarnessFacade, LoggerService, SERVICE_HARNESS_KEY],
    },
    { provide: CS_HARNESS_KEY, useFactory: (): string => getEnv(ENV_VARS.HARNESS_KEY) },
    {
      provide: SERVICE_HARNESS_KEY,
      useFactory: (): string => getEnv(ENV_VARS.SERVICE_HARNESS_KEY),
    },
    // ModalModule providers
    ModalService,
    { provide: STRING_DATE_ADAPTER_CONFIG, useValue: { outputFormat: 'yyyy-MM-dd' } },
    provideRouter(appRoutes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    provideAppInitializer(() => initApplication(inject(Store), inject(LoggerService), inject(GainsightService))()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: RequestIdInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: MaintenanceWindowInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor },
    { multi: true, provide: HTTP_INTERCEPTORS, useClass: SignalRConnectionInterceptor },
    DatePipe,
    // Toolkit v16: TypographyService must be provided at root level (see toolkit docs).
    // Provide the real DomHandler so toolkit components can perform DOM operations correctly.
    TypographyService,
    DomHandler,
    {
      provide: Renderer2,
      deps: [RendererFactory2],
      useFactory: (rf: RendererFactory2): Renderer2 => rf.createRenderer(null, null),
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    { provide: ErrorHandler, useClass: AppInsightErrorHandlerService },
    provideHttpClient(withInterceptorsFromDi()),
    // eslint-disable-next-line sonarjs/deprecation
    provideAnimations(),
  ],
})
  // eslint-disable-next-line no-console
  .catch((err) => console.error(err));
